import { productSettings } from './environment';
import { getCurrentEnvironment } from './environment.current';
import { Environments } from './environment-names';
import { ADF_STAGING_ORIGIN } from 'app/services/globals';

export interface IAzureEnvironment {
    name: string;
    authArea: string;
    artifactsEndPoint: string;
    resourceManager: string;
    aadEndpoint: string;
    viewAccountEndpoint: string;
    msalGraphEndpoint: string;
    azurePortal: string;
    portalServiceUrl: string;
    graphThumbnailUri: string;
    azureRbacManagementUrl: string;
    azureResourceDomainMap: {
        [key: string]: string;
    };
    babylonResourceEndpoint: string;
    babylonUiEnvironment: string; // PurviewTier enum
    babylonApiEnvironment: string; // PurviewEnvironment enum
    // NOTE: in fabric, this variable would be invalid in workspace private-link mode, please seize it from 
    // [pbi shell](Product/Source/app/services/iframe-messenger/iframe-messenger.extensions.ts).
    powerBIResourceId: string;
    // NOTE: in fabric, this variable would be invalid in workspace private-link mode, please seize it from 
    // [pbi shell](Product/Source/app/services/iframe-messenger/iframe-messenger.extensions.ts).
    powerBIBaseURL: string;
    // NOTE: in fabric, this variable would be invalid in workspace private-link mode, please seize it from 
    // [pbi shell](Product/Source/app/services/iframe-messenger/iframe-messenger.extensions.ts).
    FabricResourceId: string;
    // NOTE: in fabric, this variable would be invalid in workspace private-link mode, please seize it from 
    // [pbi shell](Product/Source/app/services/iframe-messenger/iframe-messenger.extensions.ts).
    FabricBaseURL: string;
    // NOTE: in fabric, this variable would be invalid in workspace private-link mode, please seize it from 
    // [pbi shell](Product/Source/app/services/iframe-messenger/iframe-messenger.extensions.ts).
    FabricPortalEndpoint: string;
    // First-party AAD app ID for Azure Data Factory's own backend (ADF PipelineManager / resourceresolver).
    // Used as the OBO assertion audience for UserAuth credentials on non-A365 (ADF/Fabric) surfaces -
    // see credential.component.ts. Must match the ADF app registration for this cloud, or AAD rejects
    // the OBO exchange with AADSTS500131 (assertion audience mismatch).
    adfFirstPartyAppId: string;
    sqlDwResourceId: string;
    cdnRootUrl: string;
    synapseStudioEndpoint: string;
    apiHub: string;
    /** @deprecated will remove*/
    fabricEndpoints: string[];
    // This endpoint is composed, it's not 100% reliable. PPE workspace has special endpoint and can be opened with public cloud Synapse Studio
    // If you use this url, make sure there is fallback logic that use the dev endpoint in workspace resource response
    composedSynapseGatewayEndpoint: (workspaceName: string) => string;
}


/** @deprecated `Use global '__IS_A365__' instead */
export function isA365ShellFunc(): boolean {
    return productSettings.value.productInfo.name !== 'adf';
}

export function isDevEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.dev || environment === Environments.a365dev;
}

export function isCIEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.ci || environment === Environments.a365ci;
}

export function isStagingEnvironment(): boolean {
    return isA365StgEnvironment() || window.location.href.startsWith(ADF_STAGING_ORIGIN);
}

export function isPublicProdEnviroment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.production || environment === Environments.a365prod;
}

export function isMooncakeEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.mooncake || environment === Environments.a365mooncake;
}

export function isFairfaxEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.fairfax || environment === Environments.a365fairfax;
}
export function isBleuEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.adfbleu || environment === Environments.a365bleu;
}
export function isDelosEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.adfdelos || environment === Environments.a365delos;
}

export function isAdfProdEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.production;
}

export function isAdfMooncakeEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.mooncake;
}

export function isAdfFairfaxEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.fairfax;
}
export function isAdfBleuEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.adfbleu;
}
export function isAdfDelosEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.adfdelos;
}

export function isA365MooncakeEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.a365mooncake;
}

export function isa365FairfaxEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.a365fairfax;
}
export function isa365BleuEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.a365bleu;
}
export function isa365DelosEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.a365delos;
}

export function isA365StgEnvironment(): boolean {
    return getCurrentEnvironment() === Environments.a365stg;
}

export function isA365LXEnvrionment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.a365usnat || environment === Environments.a365ussec;
}

export function isUsSecEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.adfussec || environment === Environments.a365ussec;
}

export function isUsNatEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.adfusnat || environment === Environments.a365usnat;
}

export function isPublicCloudEnvironment(): boolean {
    const environment = getCurrentEnvironment();
    return environment === Environments.dev
        || environment === Environments.ci
        || environment === Environments.ppe
        || environment === Environments.production
        || environment === Environments.a365dev
        || environment === Environments.a365ci
        || environment === Environments.a365stg
        || environment === Environments.a365prod;
}

export function getExtraAzureDomainMaps(envName: Environments): { [key: string]: string; } {
    switch (envName) {
        case Environments.ppe:
        case Environments.a365df:
            return {
                AzureDataExplorer: 'kusto.windows.net'
            };
        case Environments.fairfax:
        case Environments.a365fairfax:
            return {
                AzureDataExplorer: 'kusto.usgovcloudapi.net'
            };
        case Environments.mooncake:
        case Environments.a365mooncake:
            return {
                AzureDataExplorer: 'kusto.chinacloudapi.cn'
            };
        case Environments.adfusnat:
        case Environments.a365usnat:
            return {
                AzureDataExplorer: 'eaglex.ic.gov'
            };
        case Environments.adfussec:
        case Environments.a365ussec:
            return {
                AzureDataExplorer: 'core.microsoft.scloud'
            };
        case Environments.adfbleu:
        case Environments.a365bleu:
            return {
                AzureDataExplorer: 'kusto.sovcloud-api.fr' // exists
            };
        case Environments.adfdelos:
        case Environments.a365delos:
            return {
                AzureDataExplorer: 'kusto.sovcloud-api.de' // TBD - verify existence
            };
        default:
            return {
                AzureDataExplorer: 'kusto.windows.net'
            };
    }
}

export function getAllEnvironments(): Environments[] {
    return Object.values(Environments).filter((value) => typeof value === 'number')
        .reduce((prev, cur) => prev.concat(cur), []);
}

export function getAzureEnvironment(envName: Environments): IAzureEnvironment {
    switch (envName) {
        case Environments.ppe:
            return {
                name: 'Ppe',
                authArea: 'https://management.core.windows.net/',
                resourceManager: 'https://api-dogfood.resources.windows-int.net/',
                aadEndpoint: 'https://login.windows-ppe.net/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.com/',
                msalGraphEndpoint: 'https://graph.microsoft-ppe.com',
                azurePortal: 'https://df.onecloud.azure-test.net',
                babylonResourceEndpoint: 'purview.azure-test.com',
                babylonApiEnvironment: 'dogfood',
                babylonUiEnvironment: 'DEV',
                portalServiceUrl: 'https://df.datafactory.onecloud-ext.azure-test.net',
                graphThumbnailUri: 'https://graph.ppe.windows.net/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com',
                azureResourceDomainMap: {
                    CosmosDb: 'documents-staging.windows-ppe.net',
                    CosmosDbMongoDbApi: 'documents-staging.windows-ppe.net',
                    AzureSqlDatabase: 'sqltest-eg1.mscds.com',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse-dogfood.net',
                    AzureKeyVault: 'vault-int.azure-int.net',
                    AzureBlobStorage: 'core.windows.net', // No INT Storage
                    HDInsight: 'azurehdinsight.net', // No INT HDI
                    AzureSearch: 'search-dogfood.windows-int.net',
                    AzureMySql: 'mysql.database.azure.com', // No INT Azure Mysql
                    AzurePostgreSql: 'postgres.database.azure.com',
                    AzureMariaDB: 'mariadb.database.azure.com',
                    CognitiveService: 'cognitiveservices.azure.com',
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'caboaccountdogfood.net',
                    AzureML: 'ml.azure.com'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net',
                powerBIResourceId: 'https://analysis.windows-int.net/powerbi/api',
                powerBIBaseURL: 'https://powerbiapi.analysis-df.windows.net',
                FabricResourceId: 'https://analysis.windows-int.net/powerbi/api',
                FabricBaseURL: 'https://powerbiapi.analysis-df.windows.net',
                FabricPortalEndpoint: 'https://edog.analysis-df.windows.net',
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
            sqlDwResourceId: 'https://sql.azuresynapse-dogfood.net/',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.net',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: ['https://biazure-int-edog-redirect.analysis-df.windows.net'],
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse-dogfood.net`
            };
        case Environments.a365df:
            return {
                name: 'Dogfood',
                authArea: 'https://management.core.windows.net/',
                resourceManager: 'https://api-dogfood.resources.windows-int.net/',
                aadEndpoint: 'https://login.windows-ppe.net/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.com/',
                msalGraphEndpoint: 'https://graph.microsoft-ppe.com',
                azurePortal: 'https://df.onecloud.azure-test.net',
                babylonResourceEndpoint: 'purview.azure-test.com',
                babylonApiEnvironment: 'dogfood',
                babylonUiEnvironment: 'DEV',
                portalServiceUrl: 'https://df.datafactory.onecloud-ext.azure-test.net',
                graphThumbnailUri: 'https://graph.ppe.windows.net/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com',
                azureResourceDomainMap: {
                    CosmosDb: 'documents-staging.windows-ppe.net',
                    CosmosDbMongoDbApi: 'documents-staging.windows-ppe.net',
                    AzureSqlDatabase: 'sqltest-eg1.mscds.com',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse-dogfood.net',
                    AzureKeyVault: 'vault-int.azure-int.net',
                    AzureBlobStorage: 'core.windows.net', // No INT Storage
                    HDInsight: 'azurehdinsight.net', // No INT HDI
                    AzureSearch: 'search-dogfood.windows-int.net',
                    AzureMySql: 'mysql.database.azure.com', // No INT Azure Mysql
                    AzurePostgreSql: 'postgres.database.azure.com',
                    AzureMariaDB: 'mariadb.database.azure.com',
                    CognitiveService: 'cognitiveservices.azure.com',
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'caboaccountdogfood.net',
                    AzureML: 'ml.azure.com'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net',
                powerBIResourceId: 'https://analysis.windows-int.net/powerbi/api',
                powerBIBaseURL: 'https://powerbiapi.analysis-df.windows.net',
                FabricResourceId: 'https://analysis.windows-int.net/powerbi/api',
                FabricBaseURL: 'https://powerbiapi.analysis-df.windows.net',
                FabricPortalEndpoint: 'https://edog.analysis-df.windows.net',
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse-dogfood.net/',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.net',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: ['https://biazure-int-edog-redirect.analysis-df.windows.net'],
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse-dogfood.net`
            };
        case Environments.fairfax:
        case Environments.a365fairfax:
            return {
                name: 'FairfaxProd',
                authArea: 'https://management.usgovcloudapi.net/',
                resourceManager: 'https://management.usgovcloudapi.net/',
                aadEndpoint: 'https://login.microsoftonline.us/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.us/',
                msalGraphEndpoint: 'https://graph.microsoft.us',
                azurePortal: 'https://portal.azure.us',
                portalServiceUrl: 'https://s2.datafactory.ext.azure.us',
                babylonResourceEndpoint: 'purview.azure.us',
                babylonApiEnvironment: 'fairfax',
                babylonUiEnvironment: 'FF',
                graphThumbnailUri: 'https://graph.windows.net/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com',
                azureResourceDomainMap: {
                    CosmosDb: 'documents.azure.us',
                    CosmosDbMongoDbApi: 'mongo.cosmos.azure.us',
                    AzureSqlDatabase: 'database.usgovcloudapi.net',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse.usgovcloudapi.net',
                    AzureKeyVault: 'vault.usgovcloudapi.net',
                    AzureBlobStorage: 'core.usgovcloudapi.net',
                    HDInsight: 'azurehdinsight.us',
                    AzureSearch: 'search.azure.us',
                    AzureMySql: 'mysql.database.usgovcloudapi.net',
                    AzurePostgreSql: 'postgres.database.usgovcloudapi.net',
                    AzureMariaDB: 'mariadb.database.usgovcloudapi.net',
                    CognitiveService: 'cognitiveservices.azure.us',
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'azuredatalakestore.net', // no ff account
                    AzureML: 'studio.ml.azure.us'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net',
                powerBIResourceId: 'https://analysis.usgovcloudapi.net/powerbi/api',
                powerBIBaseURL: 'https://api.powerbigov.us',
                FabricResourceId: '', // to do
                FabricBaseURL: '', // to do
                FabricPortalEndpoint: 'https://app.fabric.microsoft.us',
                // Fairfax (GCC-H) ADF first-party app registration - deliberately different from the
                // commercial-cloud default. See AADSTS500131 root-cause analysis (testConnectivity failures).
                adfFirstPartyAppId: '418830e4-72c2-4a1c-befe-4d1e590b3282',
                sqlDwResourceId: 'https://sql.azuresynapse.usgovcloudapi.net',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.usgovcloudapi.net',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: [],
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse.usgovcloudapi.net`
            };
        case Environments.mooncake:
        case Environments.a365mooncake:
            return {
                name: 'MooncakeProd',
                authArea: 'https://management.core.chinacloudapi.cn/',
                resourceManager: 'https://management.chinacloudapi.cn/',
                aadEndpoint: 'https://login.partner.microsoftonline.cn/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.cn/',
                msalGraphEndpoint: 'https://microsoftgraph.chinacloudapi.cn',
                azurePortal: 'https://portal.azure.cn',
                babylonResourceEndpoint: 'purview.azure.cn',
                babylonApiEnvironment: 'mooncake',
                babylonUiEnvironment: 'MC',
                portalServiceUrl: 'https://MdpUXExtension.svc.datafactory.azure.cn',
                graphThumbnailUri: 'https://graph.chinacloudapi.cn/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com',
                azureResourceDomainMap: {
                    CosmosDb: 'documents.azure.cn',
                    CosmosDbMongoDbApi: 'mongo.cosmos.azure.cn',
                    AzureSqlDatabase: 'database.chinacloudapi.cn',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse.azure.cn',
                    AzureKeyVault: 'vault.azure.cn',
                    AzureBlobStorage: 'core.chinacloudapi.cn',
                    HDInsight: 'azurehdinsight.cn',
                    AzureSearch: 'search.azure.cn',
                    AzureMySql: 'mysql.database.chinacloudapi.cn',
                    AzurePostgreSql: 'postgres.database.chinacloudapi.cn',
                    AzureMariaDB: 'mariadb.database.chinacloudapi.cn',
                    CognitiveService: 'cognitiveservices.azure.cn',
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'azuredatalakestore.net', // no mc account
                    AzureML: 'studio.ml.azure.cn'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net', // Update later if needed
                powerBIResourceId: 'https://analysis.chinacloudapi.cn/powerbi/api',
                powerBIBaseURL: 'https://api.powerbi.cn',
                FabricResourceId: '', // to do
                FabricBaseURL: '', // to do
                FabricPortalEndpoint: 'https://app.fabric.microsoft.cn',
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse.azure.cn',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.azure.cn',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: [],
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse.azure.cn`
            };
        case Environments.adfusnat:
        case Environments.a365usnat:
            return {
                name: 'UsNatProd',
                authArea: 'https://management.core.eaglex.ic.gov/',
                resourceManager: 'https://management.azure.eaglex.ic.gov/',
                aadEndpoint: 'https://login.microsoftonline.eaglex.ic.gov/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.com/', // not available in UsNat
                msalGraphEndpoint: 'https://graph.eaglex.ic.gov',
                azurePortal: 'https://portal.azure.eaglex.ic.gov',
                babylonResourceEndpoint: 'purview.azure.com',
                babylonApiEnvironment: 'fairfax',
                babylonUiEnvironment: 'FF',
                portalServiceUrl: 'https://MdpUXExtension.svc.datafactory.azure.eaglex.ic.gov',
                graphThumbnailUri: 'https://graph.eaglex.ic.gov/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com',  // fix later when available
                azureResourceDomainMap: {
                    CosmosDb: 'cosmos.azure.eaglex.ic.gov',
                    CosmosDbMongoDbApi: 'cosmos.azure.eaglex.ic.gov',
                    AzureSqlDatabase: 'database.cloudapi.eaglex.ic.gov',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse.eaglex.ic.gov',
                    AzureKeyVault: 'vault.cloudapi.eaglex.ic.gov',
                    AzureBlobStorage: 'core.eaglex.ic.gov',
                    HDInsight: 'azurehdinsight.eaglex.ic.gov',
                    AzureSearch: 'search.windows.eaglex.ic.gov', // not available in usnat yet, need to get correct domain name
                    AzureMySql: 'mysql.database.cloudapi.eaglex.ic.gov', // Not available in UsNat yet
                    AzurePostgreSql: 'postgres.database.cloudapi.eaglex.ic.gov', // Not available in UsNat yet
                    AzureMariaDB: 'mariadb.database.cloudapi.eaglex.ic.gov',
                    CognitiveService: 'cognitiveservices.windows.eaglex.ic.gov', // Not available in UsNat yet
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'azuredatalakestore.eaglex.ic.gov',
                    AzureML: 'studio.ml.azure.us'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net',  // todo
                powerBIResourceId: 'https://analysis.eaglex.ic.gov/powerbi/api',
                powerBIBaseURL: 'https://api.powerbi.eaglex.ic.gov',
                FabricResourceId: '', // to do
                FabricBaseURL: '', // to do
                FabricPortalEndpoint: 'https://app.fabric.eaglex.ic.gov',
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse.eaglex.ic.gov',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.eaglex.ic.gov',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: [],
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse.eaglex.ic.gov`
            };
        case Environments.adfussec:
        case Environments.a365ussec:
            return {
                name: 'UsSecProd',
                authArea: 'https://management.core.microsoft.scloud/',
                resourceManager: 'https://management.azure.microsoft.scloud/',
                aadEndpoint: 'https://login.microsoftonline.microsoft.scloud/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.com/', // not available in UsSec
                msalGraphEndpoint: 'https://graph.microsoft.scloud',
                azurePortal: 'https://portal.azure.microsoft.scloud',
                babylonResourceEndpoint: 'purview.azure.com',
                babylonApiEnvironment: 'fairfax',
                babylonUiEnvironment: 'FF',
                portalServiceUrl: 'https://MdpUXExtension.svc.datafactory.azure.microsoft.scloud',
                graphThumbnailUri: 'https://graph.cloudapi.microsoft.scloud/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com', // fix later when available
                azureResourceDomainMap: {
                    CosmosDb: 'cosmos.azure.microsoft.scloud',
                    CosmosDbMongoDbApi: 'cosmos.azure.microsoft.scloud',
                    AzureSqlDatabase: 'database.cloudapi.microsoft.scloud',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse.microsoft.scloud',
                    AzureKeyVault: 'vault.cloudapi.microsoft.scloud',
                    AzureBlobStorage: 'core.microsoft.scloud',
                    HDInsight: 'azurehdinsight.microsoft.scloud',
                    AzureSearch: 'search.windows.microsoft.scloud', // not available in UsSec yet, need to get correct domain name
                    AzureMySql: 'mysql.database.cloudapi.microsoft.scloud', // Not available in UsSec yet
                    AzurePostgreSql: 'postgres.database.cloudapi.microsoft.scloud', // Not available in UsSec yet
                    AzureMariaDB: 'mariadb.database.cloudapi.microsoft.scloud',
                    CognitiveService: 'cognitiveservices.windows.microsoft.scloud', // Not available in UsSec yet
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'azuredatalakestore.microsoft.scloud',
                    AzureML: 'studio.ml.azure.us'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net',  // todo
                powerBIResourceId: 'https://analysis.microsoft.scloud/powerbi/api',
                powerBIBaseURL: 'https://api.powerbi.microsoft.scloud',
                FabricResourceId: '', // to do
                FabricBaseURL: '', // to do
                FabricPortalEndpoint: 'https://app.fabric.microsoft.scloud',
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse.microsoft.scloud',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.microsoft.scloud',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: [],
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse.microsoft.scloud`
            };
        case Environments.adfbleu:
        case Environments.a365bleu:
            return {
                name: 'BleuProd',
                authArea: 'https://management.sovcloud-api.fr/', // Exists
                resourceManager: 'https://management.sovcloud-api.fr/', // Exists
                aadEndpoint: 'https://login.sovcloud-identity.fr/', // Exists
                viewAccountEndpoint: undefined, // 'https://account.activedirectory.windowsazure.fr/',  // ?? not available in Bleu?
                msalGraphEndpoint: 'https://graph.svc.sovcloud.fr', // Exists
                azurePortal: 'https://portal.sovcloud-azure.fr', // Exists now
                portalServiceUrl: undefined, // 'https://s2.datafactory.ext.sovcloud-api.fr' // s2.datafactory.ext.azure.com does not exist in Public. Need for Bleu?
                babylonResourceEndpoint: undefined,  // No Purview found in Bleu
                babylonApiEnvironment: undefined,
                babylonUiEnvironment: undefined,
                graphThumbnailUri: 'https://directory.identity.sovcloud-api.fr/me/thumbnailPhoto?api-version=1.6', // Exists
                azureRbacManagementUrl: undefined, // 'https://stamp2.activedirectory.ext.azure.com',  // Same in all clouds in this file but does not exist
                azureResourceDomainMap: {
                    CosmosDb: 'documents.sovcloud-api.fr', // Exists
                    CosmosDbMongoDbApi: 'mongo.cosmos.sovcloud-api.fr', // Exists
                    AzureSqlDatabase: 'database.sovcloud-api.fr', // Exists
                    AzureSynapseSqlDatabase: undefined,  // sql.azuresynapse.sovcloud-api.fr not found so far
                    AzureKeyVault: 'vault.sovcloud-api.fr', // Exists
                    AzureBlobStorage: 'core.sovcloud-api.fr', // Exists
                    HDInsight: 'azurehdinsight.sovcloud-api.fr', // Exists
                    AzureSearch: undefined, // search.sovcloud-api.fr does not exist
                    AzureMySql: undefined, // mysql.database.sovcloud-api.fr does not exist
                    AzurePostgreSql: undefined, // postgres.database.sovcloud-api.fr does not exist
                    AzureMariaDB: undefined, // mariadb.database.sovcloud-api.fr does not exist
                    CognitiveService: undefined, // cognitiveservices.azure.fr not found so far
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: undefined, // azuredatalakestore.fr not found so far
                    AzureML: undefined, // studio.ml.azure.fr not found so far
                },
                artifactsEndPoint: '1ac05c7e-12d2-4605-bf9d-549d7041c6b3',
                powerBIResourceId: 'https://analysis.sovcloud-api.fr/powerbi/api', // Need to double confirm
                powerBIBaseURL: 'https://api.powerbigov.fr', // Need to double confirm
                FabricResourceId: '', // to do
                FabricBaseURL: '', // to do
                FabricPortalEndpoint: undefined, // 'https://app.fabric.sovcloud-api.fr' does not exist
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse.sovcloud-api.fr', // Need to double confirm
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.sovcloud-api.fr', // Exists
                apiHub: 'https://apihub.azure.com/', // No Bleu specific endpoint found and even in Public it is not found.
                fabricEndpoints: [],
                composedSynapseGatewayEndpoint: (workspaceName: string) => undefined // `https://${workspaceName}.dev.azuresynapse.sovcloud-api.fr`
            };
        case Environments.adfdelos:
        case Environments.a365delos:
            return {
                name: 'DelosProd',
                authArea: 'https://management.sovcloud-api.de/', // Exists
                resourceManager: 'https://management.sovcloud-api.de/', // Exists
                aadEndpoint: 'https://login.sovcloud-identity.de/', // Exists
                viewAccountEndpoint: undefined, // not available in Delos?
                msalGraphEndpoint: 'https://graph.svc.sovcloud.de', // Exists
                azurePortal: 'https://portal.sovcloud-azure.de', // Exists
                portalServiceUrl: undefined, // TBD
                babylonResourceEndpoint: undefined,  // No Purview found in Delos
                babylonApiEnvironment: undefined,
                babylonUiEnvironment: undefined,
                graphThumbnailUri: 'https://directory.identity.sovcloud-api.de/me/thumbnailPhoto?api-version=1.6', // Exists
                azureRbacManagementUrl: undefined, // TBD
                azureResourceDomainMap: {
                    CosmosDb: 'documents.sovcloud-api.de', // Exists
                    CosmosDbMongoDbApi: 'mongo.cosmos.sovcloud-api.de', // TBD - verify
                    AzureSqlDatabase: 'database.sovcloud-api.de', // Exists
                    AzureSynapseSqlDatabase: undefined,  // TBD
                    AzureKeyVault: 'vault.sovcloud-api.de', // Exists
                    AzureBlobStorage: 'core.sovcloud-api.de', // Exists
                    HDInsight: 'azurehdinsight.sovcloud-api.de', // TBD - verify
                    AzureSearch: undefined, // TBD
                    AzureMySql: undefined, // TBD
                    AzurePostgreSql: undefined, // TBD
                    AzureMariaDB: undefined, // TBD
                    CognitiveService: undefined, // TBD
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: undefined, // TBD
                    AzureML: undefined, // TBD
                },
                artifactsEndPoint: '1ac05c7e-12d2-4605-bf9d-549d7041c6b3', // Microsoft Azure Synapse Gateway FPA
                powerBIResourceId: 'https://analysis.sovcloud-api.de/powerbi/api', // TBD - verify
                powerBIBaseURL: 'https://api.powerbigov.de', // TBD - verify
                FabricResourceId: '', // to do
                FabricBaseURL: '', // to do
                FabricPortalEndpoint: undefined, // TBD
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse.sovcloud-api.de', // TBD - verify
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.sovcloud-api.de', // TBD - verify
                apiHub: 'https://apihub.azure.com/', // No Delos specific endpoint found
                fabricEndpoints: [],
                composedSynapseGatewayEndpoint: (workspaceName: string) => undefined // `https://${workspaceName}.dev.azuresynapse.sovcloud-api.de`
            };
        default:
            return {
                name: 'Prod',
                authArea: 'https://management.azure.com/',
                resourceManager: 'https://management.azure.com/',
                aadEndpoint: 'https://login.microsoftonline.com/',
                viewAccountEndpoint: 'https://account.activedirectory.windowsazure.com/',
                msalGraphEndpoint: 'https://graph.microsoft.com',
                azurePortal: 'https://portal.azure.com',
                babylonResourceEndpoint: 'purview.azure.com',
                babylonApiEnvironment: 'production',
                babylonUiEnvironment: 'PROD',
                portalServiceUrl: 'https://s2.datafactory.ext.azure.com',
                graphThumbnailUri: 'https://graph.windows.net/me/thumbnailPhoto?api-version=1.6',
                azureRbacManagementUrl: 'https://stamp2.activedirectory.ext.azure.com',
                azureResourceDomainMap: {
                    CosmosDb: 'documents.azure.com',
                    CosmosDbMongoDbApi: 'mongo.cosmos.azure.com',
                    AzureSqlDatabase: 'database.windows.net',
                    AzureSynapseSqlDatabase: 'sql.azuresynapse.net',
                    AzureKeyVault: 'vault.azure.net',
                    AzureBlobStorage: 'core.windows.net',
                    HDInsight: 'azurehdinsight.net',
                    AzureSearch: 'search.windows.net',
                    AzureMySql: 'mysql.database.azure.com',
                    AzurePostgreSql: 'postgres.database.azure.com',
                    AzureMariaDB: 'mariadb.database.azure.com',
                    CognitiveService: 'cognitiveservices.azure.com',
                    ...getExtraAzureDomainMaps(envName),
                    ADLSGen1: 'azuredatalakestore.net',
                    AzureML: 'ml.azure.com'
                },
                artifactsEndPoint: 'https://dev.azuresynapse.net',
                powerBIResourceId: 'https://analysis.windows.net/powerbi/api',
                powerBIBaseURL: 'https://api.powerbi.com',
                FabricResourceId: 'https://api.fabric.microsoft.com',
                FabricBaseURL: 'https://api.fabric.microsoft.com',
                FabricPortalEndpoint: 'https://app.fabric.microsoft.com',
                adfFirstPartyAppId: '5d13f7d7-0567-429c-9880-320e9555e5fc',
                sqlDwResourceId: 'https://sql.azuresynapse.net',
                cdnRootUrl: null,
                synapseStudioEndpoint: 'https://web.azuresynapse.net',
                apiHub: 'https://apihub.azure.com/',
                fabricEndpoints: [
                    'https://wabi-paas-1-scus-redirect.analysis.windows.net', // south central us
                    'https://wabi-us-north-central-redirect.analysis.windows.net', // north central us
                    'https://wabi-us-east2-redirect.analysis.windows.net', // east us 2
                    'https://wabi-west-us-redirect.analysis.windows.net', // west us
                    'https://wabi-west-europe-redirect.analysis.windows.net', // west europe
                    'https://wabi-north-europe-redirect.analysis.windows.net', // north europe
                    'https://wabi-brazil-south-redirect.analysis.windows.net', // brazil south
                    'https://wabi-south-east-asia-redirect.analysis.windows.net', // SEA
                    'https://wabi-australia-southeast-redirect.analysis.windows.net', // australia SE
                    'https://wabi-canada-central-redirect.analysis.windows.net', // canada central
                    'https://wabi-japan-east-redirect.analysis.windows.net', // japan east
                    'https://wabi-uk-south-redirect.analysis.windows.net', // uk south
                    'https://wabi-india-west-redirect.analysis.windows.net' // west india
                ],
                // This endpoint is comoposed, it's not 100% reliable. PPE workspace has special endpoint and can be opened with public cloud Synapse Studio
                // If you use this url, make sure there is fallback logic that use the dev endpoint in workspace resource response
                composedSynapseGatewayEndpoint: (workspaceName: string) => `https://${workspaceName}.dev.azuresynapse.net`
            };
    }
}
